import { useEffect, useMemo, useState } from "react";
import { api, frDate } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, Building2, Globe, Mail, Phone, Search, Handshake,
} from "lucide-react";

const empty = {
  name: "", partner_type: "associatif", contact_name: "",
  email: "", phone: "", website: "", address: "",
  logo_base64: "", description: "", collaboration: "",
  start_date: new Date().toISOString().slice(0, 10),
  status: "actif", notes: "",
};

const TYPES = {
  institutionnel: { label: "Institutionnel", bg: "bg-brand-navy/10 text-brand-navy" },
  associatif: { label: "Associatif", bg: "bg-brand-green/10 text-brand-green" },
  entreprise: { label: "Entreprise", bg: "bg-brand-orange/10 text-brand-orange" },
  collectivite: { label: "Collectivité", bg: "bg-blue-100 text-blue-700" },
  mecene: { label: "Mécène", bg: "bg-amber-100 text-amber-700" },
  media: { label: "Média", bg: "bg-purple-100 text-purple-700" },
  autre: { label: "Autre", bg: "bg-gray-100 text-gray-700" },
};
const STATUS = {
  actif: { label: "Actif", bg: "bg-brand-green text-white" },
  inactif: { label: "Inactif", bg: "bg-gray-200 text-gray-700" },
  en_discussion: { label: "En discussion", bg: "bg-brand-orange text-white" },
};

export default function Partenaires() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(empty);
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");

  const load = () => api.get("/partners").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const onLogo = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) { toast.error("Image trop lourde (max 2 Mo)"); return; }
    const r = new FileReader();
    r.onload = () => setForm((s) => ({ ...s, logo_base64: r.result }));
    r.readAsDataURL(f);
  };

  const save = async () => {
    if (!form.name) { toast.error("Nom du partenaire requis"); return; }
    try {
      if (edit) await api.put(`/partners/${edit.id}`, form);
      else await api.post("/partners", form);
      toast.success(edit ? "Partenaire modifié." : "Partenaire ajouté.");
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Supprimer "${p.name}" ?`)) return;
    await api.delete(`/partners/${p.id}`); load();
  };

  const filtered = useMemo(() => rows.filter((p) => {
    const matchesType = typeFilter === "all" || p.partner_type === typeFilter;
    const s = `${p.name} ${p.contact_name} ${p.email}`.toLowerCase();
    return matchesType && s.includes(q.toLowerCase());
  }), [rows, q, typeFilter]);

  const counts = useMemo(() => {
    const c = { total: rows.length, actif: 0 };
    rows.forEach((p) => { if (p.status === "actif") c.actif++; });
    return c;
  }, [rows]);

  return (
    <div data-testid="partenaires-page">
      <PageHeader
        eyebrow="Écosystème"
        title="Partenaires de travail"
        description="Référencez les partenaires institutionnels, associatifs, mécènes et entreprises qui accompagnent l'association."
        actions={
          <Button data-testid="new-partner-btn" onClick={() => { setEdit(null); setForm(empty); setOpen(true); }} className="bg-brand-orange hover:bg-[#D04A06] text-white">
            <Plus size={16} className="mr-1" /> Nouveau partenaire
          </Button>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white border border-brand-border rounded-xl p-5">
          <div className="small-caps">Total partenaires</div>
          <div className="font-serif text-3xl text-brand-navy mt-1">{counts.total}</div>
        </div>
        <div className="bg-white border border-brand-border rounded-xl p-5">
          <div className="small-caps">Partenariats actifs</div>
          <div className="font-serif text-3xl text-brand-green mt-1">{counts.actif}</div>
        </div>
        <div className="bg-white border border-brand-border rounded-xl p-5 md:col-span-2 flex items-center gap-3 text-brand-navy">
          <Handshake size={22} className="text-brand-orange" />
          <p className="text-sm leading-relaxed">
            Les partenaires renforcent la mission de l'association et facilitent l'accès aux ressources, aux lieux et aux publics.
          </p>
        </div>
      </div>

      <div className="bg-white border border-brand-border rounded-xl p-3 mb-6 flex flex-wrap items-center gap-2">
        <Search size={16} className="text-gray-400 ml-2" />
        <Input
          data-testid="search-partner-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Rechercher un partenaire..."
          className="border-0 focus-visible:ring-0 shadow-none flex-1 min-w-[200px]"
        />
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les types</SelectItem>
            {Object.entries(TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white border border-brand-border rounded-xl p-10 text-center text-gray-400">
          Aucun partenaire {rows.length > 0 ? "ne correspond à votre recherche" : "pour l'instant"}.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((p) => (
            <div key={p.id} data-testid={`partner-card-${p.id}`} className="bg-white border border-brand-border rounded-xl p-5 hover:shadow-md transition-shadow flex flex-col">
              <div className="flex items-start gap-4 mb-3">
                <div className="w-14 h-14 rounded-lg border border-brand-border bg-gray-50 flex items-center justify-center overflow-hidden shrink-0">
                  {p.logo_base64 ? (
                    <img src={p.logo_base64} alt={p.name} className="w-full h-full object-contain" />
                  ) : (
                    <Building2 size={22} className="text-gray-400" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-serif text-xl text-brand-navy truncate">{p.name}</h3>
                  <div className="flex flex-wrap gap-1 mt-1">
                    <Badge className={TYPES[p.partner_type]?.bg}>{TYPES[p.partner_type]?.label}</Badge>
                    <Badge className={STATUS[p.status]?.bg}>{STATUS[p.status]?.label}</Badge>
                  </div>
                </div>
              </div>

              {p.description && <p className="text-sm text-gray-600 mb-3 line-clamp-2">{p.description}</p>}
              {p.collaboration && (
                <div className="mb-3 text-xs text-gray-500">
                  <span className="small-caps">Collaboration</span>
                  <div className="mt-1 text-gray-700">{p.collaboration}</div>
                </div>
              )}

              <div className="text-xs text-gray-500 space-y-1 mt-auto">
                {p.contact_name && <div>Contact : <span className="text-gray-700">{p.contact_name}</span></div>}
                {p.email && <div className="inline-flex items-center gap-1"><Mail size={11} /> <a href={`mailto:${p.email}`} className="text-brand-navy hover:underline">{p.email}</a></div>}
                {p.phone && <div className="inline-flex items-center gap-1"><Phone size={11} /> {p.phone}</div>}
                {p.website && <div className="inline-flex items-center gap-1"><Globe size={11} /> <a href={p.website.startsWith("http") ? p.website : `https://${p.website}`} target="_blank" rel="noreferrer" className="text-brand-navy hover:underline truncate max-w-[200px]">{p.website}</a></div>}
                {p.start_date && <div>Partenaire depuis {frDate(p.start_date)}</div>}
              </div>

              <div className="flex gap-2 mt-4 pt-3 border-t border-brand-border">
                <Button size="sm" variant="outline" className="flex-1" onClick={() => { setEdit(p); setForm({ ...empty, ...p }); setOpen(true); }}>
                  <Pencil size={14} className="mr-1" /> Modifier
                </Button>
                <Button size="sm" variant="ghost" onClick={() => remove(p)}>
                  <Trash2 size={14} className="text-red-500" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="font-serif text-2xl text-brand-navy">{edit ? "Modifier le partenaire" : "Nouveau partenaire"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
            <div className="md:col-span-2 flex items-center gap-4">
              <div className="w-20 h-20 rounded-lg border border-brand-border overflow-hidden bg-gray-50 flex items-center justify-center">
                {form.logo_base64 ? (
                  <img src={form.logo_base64} alt="" className="w-full h-full object-contain" />
                ) : (
                  <Building2 size={28} className="text-gray-400" />
                )}
              </div>
              <div>
                <Label>Logo du partenaire</Label>
                <Input type="file" accept="image/*" onChange={onLogo} className="mt-1" />
                <p className="text-xs text-gray-400 mt-1">PNG ou JPG — 2 Mo max.</p>
              </div>
            </div>

            <div className="md:col-span-2"><Label>Nom du partenaire *</Label><Input data-testid="partner-name-input" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} /></div>
            <div><Label>Type</Label>
              <Select value={form.partner_type} onValueChange={(v) => setForm({...form, partner_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Statut</Label>
              <Select value={form.status} onValueChange={(v) => setForm({...form, status: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Personne de contact</Label><Input value={form.contact_name} onChange={(e) => setForm({...form, contact_name: e.target.value})} /></div>
            <div><Label>Début du partenariat</Label><Input type="date" value={form.start_date || ""} onChange={(e) => setForm({...form, start_date: e.target.value})} /></div>
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} /></div>
            <div><Label>Téléphone</Label><Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} /></div>
            <div className="md:col-span-2"><Label>Site web</Label><Input value={form.website} onChange={(e) => setForm({...form, website: e.target.value})} placeholder="https://..." /></div>
            <div className="md:col-span-2"><Label>Adresse</Label><Input value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} /></div>
            <div className="md:col-span-2"><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} placeholder="Quelques mots sur le partenaire..." /></div>
            <div className="md:col-span-2"><Label>Nature de la collaboration</Label><Textarea rows={2} value={form.collaboration} onChange={(e) => setForm({...form, collaboration: e.target.value})} placeholder="Soutien financier, mise à disposition de salle, co-organisation d'événements..." /></div>
            <div className="md:col-span-2"><Label>Notes internes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button data-testid="save-partner-btn" onClick={save} className="bg-brand-orange hover:bg-[#D04A06] text-white">{edit ? "Enregistrer" : "Ajouter le partenaire"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
